import React, { useMemo } from 'react';
import { Box, Button, Image, Typography } from '@lucid/lego';
import type { TransformedCheckoutDetail } from '../../../api/services/lupp/inventory/transformers.checkout';
import {
  NL_NON_TAXABLE_FEE_CODES,
  NL_STATUTORY_FEE_CODES,
  NL_TAXABLE_FEE_CODES,
} from '../../../api/services/lupp/inventory/dto.inventory';
import Feature from './Feature/Feature';
import Price from './Price/Price';
import styles from './VehicleCard.module.scss';
import {
  DOCUSIGN_SENT_STATUSES,
  PARTNER_COMPENSATION_PERCENTAGE,
  CHARGING_CREDIT_OFFER_MY26_AMOUNT,
  CHARGING_CREDIT_OFFER_MY27_AMOUNT,
  CHARGING_VOUCHER_VALUE,
} from '../constants';
import { parseLocalePrice, resolveNlFeeLabel, resolveVatLabel } from '../../../utils/tools';
import { buildGrandTotalCalculationParams, calculatePricing } from '../utils';

/**
 * Labels type for i18n support
 */
type Labels = Record<string, string> | undefined;

type Theme = 'dark' | 'light';

type PricingSummaryItem = {
  label: string;
  value: number;
};

type VehicleCardProps = {
  /** Theme for the component */
  theme: Theme;
  /** Checkout data from the API */
  checkoutData?: TransformedCheckoutDetail | null;
  /** Vehicle inventory data for fallback to year when checkoutData.modelYear unavailable */
  vehicle?: { year?: string } | null;
  /** Quote ID - required for download functionality */
  quoteId?: string;
  /** Callback when download quotation button is clicked */
  onDownloadQuotation?: () => void;
  /** Callback when send docusign button is clicked */
  onSendDocusign?: () => void;
  /** Current docusign status */
  docusignStatus?: string;
  /** Whether the quote has been saved successfully */
  isQuoteSaved?: boolean;
  /** Current status of the quote - buttons disabled when not 'Active' */
  currentStatus?: string;
  /** Whether the vehicle is unavailable */
  isVehicleUnavailable?: boolean;
  /** Cash discount amount entered in the form */
  cashDiscount?: string;
  /** Percentage discount entered in the form */
  percentageDiscount?: string;
  /** Agent subvention amount entered in the form */
  agentSubventionAmount?: string;
  /** Preferred payment method selected by the user */
  preferredPaymentMethod?: string;
  /** Selected requested incentives (e.g. trade_in_support) */
  requestedIncentives?: string[];
  /** Test ID for testing */
  dataTestId?: string;
  /** i18n labels from Contentful */
  labels?: Labels;
};

/**
 * Vehicle card component displaying vehicle configuration and pricing summary
 * @component
 * @param {VehicleCardProps} props - Component props
 * @returns {JSX.Element} Vehicle card with image, variant details, pricing, and action buttons
 * @example
 * <VehicleCard
 *   theme="dark"
 *   checkoutData={checkoutData}
 *   quoteId="quote-123"
 *   onDownloadQuotation={handleDownload}
 *   onSendDocusign={handleSendDocusign}
 *   dataTestId="vehicle-card"
 * />
 */

const VehicleCard: React.FC<VehicleCardProps> = ({
  theme,
  checkoutData,
  vehicle,
  quoteId,
  onDownloadQuotation,
  onSendDocusign,
  docusignStatus,
  isQuoteSaved = false,
  currentStatus,
  isVehicleUnavailable = false,
  cashDiscount,
  percentageDiscount,
  agentSubventionAmount,
  preferredPaymentMethod,
  requestedIncentives,
  dataTestId = 'vehicle-card',
  labels,
}) => {
  // Disable buttons when currentStatus exists and is not 'Active'
  const isNotActive = currentStatus ? currentStatus !== 'Active' : false;

  // Calculate price adjustment based on form inputs - memoized for performance
  const priceAdjustment = useMemo(() => {
    const subtotal = checkoutData?.pricing?.configurationSubtotal ?? 0;
    let cashDiscountValue = parseLocalePrice(cashDiscount);
    let percentageDiscountValue = parseLocalePrice(percentageDiscount);
    const agentSubventionValue = parseLocalePrice(agentSubventionAmount);

    const isCashDiscountInAmount = cashDiscountValue > 0;
    let isPercentageDiscountExceed = false;

    // Calculate cash discount from percentage if no cash amount entered
    if (!isCashDiscountInAmount && percentageDiscountValue > 0) {
      // "percentage"
      const clampedPercentage = Math.min(percentageDiscountValue, 100);
      isPercentageDiscountExceed = clampedPercentage > PARTNER_COMPENSATION_PERCENTAGE;
      cashDiscountValue = (clampedPercentage / 100) * subtotal;
    } else if (isCashDiscountInAmount) {
      // "amount"
      const clampedCash = Math.min(cashDiscountValue, subtotal);
      percentageDiscountValue = (clampedCash / subtotal) * 100;
      isPercentageDiscountExceed = percentageDiscountValue > PARTNER_COMPENSATION_PERCENTAGE;
    }

    // Use either Cash/Percentage Discount OR Agent Subvention Amount (mutually exclusive)
    // Cash Discount is for 'Cash' and '3rd Party Financing' payment methods
    // Agent Subvention is for 'Loan', 'Leasing', etc. payment methods
    const requestedDiscountValue =
      cashDiscountValue > 0 || percentageDiscountValue > 0
        ? cashDiscountValue
        : agentSubventionValue;
    const totalDiscountValue = Math.min(Math.max(0, requestedDiscountValue), Math.max(0, subtotal));

    return { discountValue: totalDiscountValue, isPercentageDiscountExceed };
  }, [
    checkoutData?.pricing?.configurationSubtotal,
    cashDiscount,
    percentageDiscount,
    agentSubventionAmount,
  ]);

  // VAT calculation based on subtotal after price adjustments - memoized for performance
  const vatCalculation = useMemo(() => {
    const isLfsLease = preferredPaymentMethod?.toLowerCase() === 'lfs lease';
    const priceAdjustmentAmount = isLfsLease ? 0 : priceAdjustment?.discountValue || 0;
    const calculation = calculatePricing(
      buildGrandTotalCalculationParams(checkoutData?.pricing, {
        priceAdjustmentValue: priceAdjustmentAmount,
        preferredPaymentMethod,
        requestedIncentives,
      })
    );

    return {
      subtotalExclVat: calculation.subtotalExclVat,
      roundedVat: calculation.vatAmount,
      grandTotal: calculation.grandTotal,
      taxRatePercent: checkoutData?.pricing?.taxRate ?? 0,
    };
  }, [
    checkoutData?.pricing,
    priceAdjustment?.discountValue,
    preferredPaymentMethod,
    requestedIncentives,
  ]);

  // Shared button state calculations
  const hasQuote = Boolean(quoteId || isQuoteSaved);
  const isDocusignAlreadySent = docusignStatus
    ? DOCUSIGN_SENT_STATUSES.includes(docusignStatus)
    : false;
  const isDocusignDisabled =
    !hasQuote || isNotActive || isDocusignAlreadySent || isVehicleUnavailable;

  // Charging Credit Offer amount based on vehicle model year
  const chargingCreditAmount = useMemo(() => {
    if (!requestedIncentives?.includes(CHARGING_VOUCHER_VALUE)) return 0;
    const modelYear = checkoutData?.modelYear || vehicle?.year || '';
    if (modelYear === '2026') return CHARGING_CREDIT_OFFER_MY26_AMOUNT;
    if (modelYear === '2027') return CHARGING_CREDIT_OFFER_MY27_AMOUNT;
    return 0;
  }, [requestedIncentives, checkoutData?.modelYear, vehicle?.year]);

  if (!checkoutData) {
    return null;
  }

  const isNlPricing = checkoutData.pricing.fees.some(
    (fee) => fee.code && NL_STATUTORY_FEE_CODES.has(fee.code)
  );
  const nlTaxableFees = checkoutData.pricing.fees.filter(
    (fee) => fee.code && NL_TAXABLE_FEE_CODES.has(fee.code)
  );
  const nlNonTaxableFees = checkoutData.pricing.fees.filter(
    (fee) => fee.code && NL_NON_TAXABLE_FEE_CODES.has(fee.code)
  );

  return (
    <Box className={styles.cardContainer} data-testid={dataTestId}>
      {/* Vehicle Image */}
      {checkoutData.vehicleImage && (
        <Box className={styles.vehicleImageContainer}>
          <Image
            uri_desktop={{ '1x': checkoutData.vehicleImage, '2x': checkoutData.vehicleImage }}
            uri_mobile={{ '1x': checkoutData.vehicleImage, '2x': checkoutData.vehicleImage }}
            alt={`${checkoutData.model} ${checkoutData.trim}`}
            className={styles.vehicleImage}
            aspectRatio={{ ratio: '16:10' }}
            rounded={false}
          />
        </Box>
      )}
      <Box>
        {/* Vehicle Variant Details */}
        {checkoutData?.vehicleOptions.map((option) => (
          <Feature
            key={option.code}
            description={option.name}
            price={option.value}
            theme={theme}
            labels={labels}
          />
        ))}

        {/* Features (sound system, driver assistance, packages, seating, etc.) */}
        {checkoutData?.features.map((feature) => (
          <Feature
            key={feature.code}
            description={feature.name}
            price={feature.value}
            theme={theme}
            labels={labels}
          />
        ))}

        {/* Divider */}
        <Box className={styles.divider} mt={16} mb={16} />
        {/* Configuration Subtotal */}
        <Box className={styles.subtotalRow} mt={8}>
          <Price
            label={labels?.['vehicle.configurationPriceExclVat'] ?? 'Configuration Price excl VAT'}
            value={checkoutData?.pricing?.configurationSubtotal ?? 0}
            theme={theme}
            isBold={true}
            skipFormatting
          />
        </Box>
      </Box>
      {/* Pricing Summary */}
      <Box className={styles.pricingSummary} mt={8}>
        {/* Divider */}
        <Box className={styles.divider} mt={16} mb={16} />
        <>
          {checkoutData.pricing.credits.map((credit: PricingSummaryItem, index: number) => (
            <Price
              key={`credit-${index}`}
              label={credit.label}
              value={credit.value}
              theme={theme}
              isNegative
            />
          ))}
          {chargingCreditAmount > 0 && !preferredPaymentMethod?.toLowerCase().includes('lease') && (
            <>
              <Price
                label={labels?.['vehicle.chargingCredit'] ?? 'Charging Credit'}
                value={chargingCreditAmount}
                theme={theme}
                skipFormatting
              />
              <Price
                label={labels?.['vehicle.chargingCreditOffer'] ?? 'Charging Credit Offer'}
                value={chargingCreditAmount}
                theme={theme}
                isNegative
                skipFormatting
              />
            </>
          )}
          {preferredPaymentMethod?.toLowerCase() !== 'lfs lease' && (
            <Price
              label={labels?.['vehicle.priceAdjustment'] ?? 'Price Adjustment'}
              value={priceAdjustment?.discountValue || 0}
              theme={theme}
              isNegative
              skipFormatting
            />
          )}
          {isNlPricing &&
            nlTaxableFees.map((fee, index) => (
              <Price
                key={`nl-taxable-fee-${index}`}
                label={resolveNlFeeLabel(fee.label, labels)}
                value={fee.value}
                theme={theme}
                skipFormatting
              />
            ))}
          <Price
            label={labels?.['vehicle.nlSubtotalExclVat'] ?? 'Subtotal excl VAT'}
            value={vatCalculation.subtotalExclVat}
            theme={theme}
            isBold
            skipFormatting
          />
          <Price
            label={`${resolveVatLabel(labels)} (${vatCalculation.taxRatePercent}%)`}
            value={vatCalculation.roundedVat}
            theme={theme}
            skipFormatting
          />
          {isNlPricing &&
            nlNonTaxableFees.map((fee, index) => (
              <Price
                key={`nl-non-taxable-fee-${index}`}
                label={resolveNlFeeLabel(fee.label, labels)}
                value={fee.value}
                theme={theme}
                skipFormatting
              />
            ))}
          <Box className={styles.divider} mt={16} mb={16} />
          <Price
            label={labels?.['vehicle.totalInclVat'] ?? 'Total incl VAT*'}
            value={vatCalculation.grandTotal}
            theme={theme}
            isBold
            skipFormatting
          />
        </>

        <Typography variant="body" size="small" color="gray-5" theme={theme}>
          {labels?.['vehicle.totalNote'] ??
            '* Total price including VAT, destination, roadworthy, delivery, documentation and registration costs.'}
        </Typography>
      </Box>
      {/* Action Buttons */}
      <Box className={styles.buttonContainer} pt={24} pb={24} pl={2} pr={2}>
        <Button
          label={labels?.['vehicle.downloadQuotation'] ?? 'Download Quotation'}
          type="secondary"
          color={theme === 'dark' ? 'neutral' : 'brand'}
          size="medium"
          theme={theme === 'dark' ? 'light' : 'dark'}
          className={theme === 'light' ? styles.downloadButtonLight : undefined}
          handleClick={hasQuote ? onDownloadQuotation : undefined}
          disabled={!hasQuote}
          gaComponent="vehicle-card"
          dataTestId={`${dataTestId}-download-btn`}
        />
        <Button
          label={labels?.['vehicle.sendDocusign'] ?? 'Send Docusign'}
          type="primary"
          color={theme === 'dark' ? 'neutral' : 'brand'}
          size="medium"
          theme={theme === 'dark' ? 'light' : 'dark'}
          handleClick={!isDocusignDisabled ? onSendDocusign : undefined}
          disabled={isDocusignDisabled}
          gaComponent="vehicle-card"
          dataTestId={`${dataTestId}-send-btn`}
        />
      </Box>
    </Box>
  );
};

VehicleCard.displayName = 'VehicleCard';

export default VehicleCard;
